/*
  OBx PDP buy box adopters.

  The financing rows and the add-on rows in this design are not new content.
  Affirm, Shop Pay Installments, Clicklease and the Advanced Product Options
  app each already render their own node into the buy box, on their own
  schedule, after their own script loads. Reimplementing any of them would
  fork cart behaviour and monthly-payment maths away from the source of
  truth, so instead each panel renders empty slots and this file moves the
  real provider node into the matching slot as soon as it shows up.

  Timing is the hard part. Measured on this store's product page:
  DOMContentLoaded ~4.4s, load ~11.4s, and the options app plus Affirm are
  not ready until ~11.7s. So a single window after DOMContentLoaded is not
  enough, and this watches the buy box container until everything lands.

  Consequences worth knowing:
  - Nothing is cloned. Cloning a custom element (shopify-payment-terms) or an
    app input detaches it from its own script and from the product form.
  - Every adopt is idempotent, so re-running it is always safe.
  - A provider that never appears leaves its row hidden, and the financing
    badge counts only the rows that actually filled.
  - The whole file is wrapped in an IIFE. Top-level const in a classic script
    lands in the shared global scope, so if this file is ever served twice on
    one page the second parse would throw "already been declared" and take
    its whole script tag with it.
  - The throttle deliberately uses setTimeout, not requestAnimationFrame.
    rAF does not fire in a background tab, which would leave the throttle
    latched and silently abandon every later mutation.
*/
;(function () {
  if (window.__obxBuyBoxInitialized) return
  window.__obxBuyBoxInitialized = true

  const ADDONS_APP_SELECTOR = '#avpoptions-container__v2'
  const BUY_BOX_SELECTOR = '.product-single__meta'
  const THROTTLE_MS = 50

  // Safety net for anything that renders outside the observed container, or
  // later than the observer expects. adoptAll is idempotent, so the cost of
  // an unnecessary pass is a handful of querySelector calls.
  const RECHECK_DELAYS = [0, 500, 1500, 3000, 6000, 10000, 15000, 22000, 30000, 45000]

  let throttled = false
  let observer = null
  let loadHooked = false

  function initObxBuyBox() {
    if (!document.querySelector('[data-obx-slot]') && !document.querySelector('[data-obx-addons]')) {
      return
    }

    adoptAll()

    // Every provider renders inside the buy box, so watching that container
    // is both sufficient and cheap enough to leave connected for the life of
    // the page. That removes any need to guess at a cutoff, and it also
    // catches Clicklease re-inserting its button after a variant change.
    // Re-attached on section:load, because the editor replaces the container
    // and the old one is then detached and silent.
    const container = document.querySelector(BUY_BOX_SELECTOR) || document.body

    if (observer) observer.disconnect()
    observer = new MutationObserver(schedule)
    observer.observe(container, { childList: true, subtree: true })

    if (!loadHooked) {
      loadHooked = true
      window.addEventListener('load', adoptAll)
    }

    RECHECK_DELAYS.forEach((delay) => setTimeout(adoptAll, delay))
  }

  function schedule() {
    if (throttled) return
    throttled = true

    setTimeout(() => {
      throttled = false
      adoptAll()
    }, THROTTLE_MS)
  }

  /* One mistake in a third-party's markup must not stop the other panels, and
     must never leave the throttle latched. */
  function adoptAll() {
    try {
      adoptSlots()
    } catch (error) {
      console.warn('[obx-buy-box] slot adopt failed', error)
    }

    try {
      document.querySelectorAll('[data-obx-addons]').forEach(adoptAddons)
    } catch (error) {
      console.warn('[obx-buy-box] add-ons adopt failed', error)
    }

    try {
      syncAtcPrice()
    } catch (error) {
      console.warn('[obx-buy-box] add to cart price sync failed', error)
    }
  }

  /* ---------------------------------------------------------- slot adopt */

  /* Used by the financing rows (Affirm, Shop Pay, Clicklease) and by the
     trust panel's pickup row, which adopts the theme's <store-availability>. */
  function adoptSlots() {
    document.querySelectorAll('[data-obx-slot]').forEach((slot) => {
      const source = document.querySelector(slot.dataset.obxSlot)

      // Already adopted, or the provider has not rendered yet.
      if (!source || slot.contains(source)) return

      // Affirm and store-availability both render their element first and
      // fill it later. Adopting at that point would reveal an empty row.
      if (!hasContent(source)) return

      slot.appendChild(source)

      const row = slot.closest('[data-obx-row]')

      if (row) {
        setHidden(row, false)
        wireRowAction(row, slot)
      }
    })

    document.querySelectorAll('[data-obx-financing]').forEach(updateFinancingBadge)
  }

  /* Shop Pay keeps its content in a shadow root and Clicklease renders an
     SVG, so text alone is not a fair test of whether a provider is done. The
     descendant check covers a provider that wraps its custom element in a
     plain div. */
  function hasContent(node) {
    if (node.textContent.trim().length > 0) return true
    if (node.shadowRoot) return true
    if (node.querySelector('svg, img, iframe')) return true

    return Array.prototype.some.call(node.querySelectorAll('*'), (child) => child.shadowRoot)
  }

  /* The design puts a VIEW PLANS / APPLY button on each row. Each provider
     already ships its own trigger inside the node we just adopted, so
     forward the click there instead of duplicating a modal or an affiliate
     URL. */
  function wireRowAction(row, slot) {
    const action = row.querySelector('[data-obx-action]')
    if (!action || action.dataset.obxWired === 'true') return

    action.dataset.obxWired = 'true'
    action.addEventListener('click', (event) => {
      event.preventDefault()

      const trigger = findTrigger(slot)

      if (trigger) {
        activate(trigger)
        return
      }

      if (action.dataset.obxFallbackHref) {
        window.open(action.dataset.obxFallbackHref, '_blank', 'noopener')
      }
    })
  }

  /* Affirm's .affirm-modal-trigger is an <a> with no href, so this cannot be
     narrowed to a[href]. Clicklease puts its handler in an onclick attribute
     on an <svg>. Shop Pay is the awkward one: it renders its whole banner,
     button included, inside an open shadow root, and querySelector does not
     cross a shadow boundary, so the row's button had nothing to forward to. */
  function findTrigger(slot) {
    const selector = 'a, button, [onclick], [role="button"]'
    const direct = slot.querySelector(selector)

    if (direct) return direct

    const hosts = slot.querySelectorAll('*')

    for (let i = 0; i < hosts.length; i++) {
      const shadow = hosts[i].shadowRoot

      if (shadow) {
        const inShadow = shadow.querySelector(selector)

        if (inShadow) return inShadow
      }
    }

    return null
  }

  /* SVGElement has no click() method, so calling it on Clicklease's <svg>
     threw and the Apply button did nothing. A real mouse event activates both
     kinds of element, but click() is kept where it exists so anchors and
     buttons behave exactly as a genuine press would. */
  function activate(el) {
    if (typeof el.click === 'function') {
      el.click()
      return
    }

    el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))
  }

  /* INVARIANT for everything in this file: never write to the DOM unless the
     value actually changes.

     The observer watches the buy box, so any unconditional write inside it
     re-triggers this whole pass and the pass writes again. Assigning
     textContent counts even when the string is identical, because the setter
     replaces the child text node either way. Getting this wrong produced a
     permanent loop firing as fast as the throttle allowed. */
  function setText(el, value) {
    if (el.textContent !== value) {
      el.textContent = value
    }
  }

  function setHidden(el, value) {
    if (el.hidden !== value) {
      el.hidden = value
    }
  }

  function updateFinancingBadge(root) {
    const badge = root.querySelector('[data-obx-badge]')
    if (!badge) return

    const live = root.querySelectorAll('[data-obx-row]:not([hidden])').length

    setHidden(badge, live === 0)
    setText(badge, badge.dataset.obxBadge.replace('[count]', live))
  }

  /* ------------------------------------------------------------ add-ons */

  function adoptAddons(root) {
    const app = document.querySelector(ADDONS_APP_SELECTOR)
    const list = root.querySelector('[data-obx-addons-list]')
    const promoted = document.querySelector('[data-obx-promoted]')

    /* Swatch options are visual pickers that do not fit the panel's checkbox
       rows. On a product that renders them the panel's rows are left exactly
       where the app put them, but the app's dropdowns are still promoted into
       the field pair above, which is the same treatment they get on every
       other product. */
    const hasSwatches = !!(app && app.querySelector('.ap-options__swatch'))

    if (hasSwatches) {
      // The app can render its option types in stages, so an earlier pass may
      // already have adopted checkbox rows. Hand those back, or hiding the
      // panel would hide controls the customer still needs to reach.
      releaseAddons(app, list)
    }

    if (app) {
      app.querySelectorAll('.avp-option').forEach((option) => {
        const isSelect = option.classList.contains('ap-options__select-container')

        // On a swatch product the dropdowns still move; nothing else does.
        if (hasSwatches && !isSelect) return

        const target = isSelect ? promoted : list

        if (!target || target.contains(option)) return

        target.appendChild(option)
        makeTooltipFocusable(option)
        if (!isSelect) addPriceLabel(option)
      })
    }

    /* One rule decides whether the panel is shown: does it actually hold any
       rows. That covers every empty case without a branch each, including a
       product with no options app, one whose options are all dropdowns and so
       all end up promoted above, and the swatch case where the rows are
       deliberately left with the app. It also means the panel stays hidden
       during the ~11s the app takes to render rather than sitting there as an
       empty header. root is the panel already passed in. */
    const rows = list ? list.querySelectorAll('.avp-option').length : 0

    setHidden(root, rows === 0)
    updateAddonsCount(root, list)
  }

  /* Puts adopted checkbox rows back in the app's container and strips the
     price labels we added, so the app's own layout takes over cleanly.
     Promoted dropdowns are deliberately left where they are, because they keep
     their promoted placement on swatch products too. Order is not restored,
     which is acceptable for a path that only runs when swatch options appear
     after checkbox rows were already adopted. */
  function releaseAddons(app, list) {
    if (!app || !list) return

    list.querySelectorAll('.avp-option').forEach((option) => {
      const price = option.querySelector('.obx-bb__addon-price')

      if (price) price.remove()
      app.appendChild(option)
    })
  }

  /* The app's tooltip trigger is a plain div, so once the description is only
     reachable through it, keyboard users have no way in. The CSS reveals on
     :focus-within, which needs something focusable inside. */
  function makeTooltipFocusable(option) {
    const tooltip = option.querySelector('.ap-tooltip')
    if (!tooltip || tooltip.hasAttribute('tabindex')) return

    const description = tooltip.querySelector('.apo-tooltip-title')

    tooltip.setAttribute('tabindex', '0')
    tooltip.setAttribute('role', 'note')

    if (description) {
      tooltip.setAttribute('aria-label', description.textContent.trim())
    }
  }

  /* The app prints its price inside the checkbox label ("Yes (+$200.00)").
     The design wants it right-aligned and bare, and data-variant-price gives
     the amount in cents without parsing display text. */
  function addPriceLabel(option) {
    if (option.querySelector('.obx-bb__addon-price')) return

    const input = option.querySelector('input[type="checkbox"]')
    if (!input) return

    const cents = parseInt(input.dataset.variantPrice, 10)
    if (Number.isNaN(cents)) return

    const price = document.createElement('span')
    price.className = 'obx-bb__addon-price'

    const root = option.closest('[data-obx-addons]')

    if (cents === 0) {
      price.classList.add('obx-bb__addon-price--free')
      price.textContent = (root && root.dataset.obxFreeLabel) || 'Free'
    } else {
      price.textContent = `+${formatAmount(cents, root && root.dataset.obxCurrency)}`
    }

    option.appendChild(price)
  }

  /* Matches the design, which drops the decimals on whole amounts (+$200).
     Currency comes from Liquid rather than being hardcoded, and Intl keeps
     the thousands separator that a toFixed() string would lose. */
  function formatAmount(cents, currency) {
    const amount = cents / 100
    const digits = Number.isInteger(amount) ? 0 : 2

    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    })
  }

  function updateAddonsCount(root, list) {
    const note = root.querySelector('[data-obx-count]')
    if (!note || !list) return

    const count = list.querySelectorAll('.avp-option').length

    setHidden(note, count === 0)
    setText(note, note.dataset.obxCount.replace('[count]', count))
  }

  /* ------------------------------------------------- add to cart price */

  /* The CTA row's layout is Liquid and CSS. SC-block-buy-buttons.liquid renders
     the shipping note under Buy It Now and the price span inside the add to
     cart button, and the wishlist app places its own button through its
     product_css_selector setting.

     What is left here is content, not layout: keeping that price label in step
     with the selected variant. The theme's variant handler rewrites the
     button's first span from its locale strings and leaves this one alone, so
     without this it would go stale the moment someone switches variant.

     The figure is mirrored from the rendered price rather than recomputed from
     variant.price, because this store runs a currency converter (Transcy), so
     the price block is the one thing guaranteed to agree with what the
     customer is actually reading. It also picks up any add-on the options app
     prices in. */
  function syncAtcPrice() {
    const el = document.querySelector('block-buy-buttons .obx-bb__atc-price')
    if (!el) return

    const price = currentPriceText()
    if (!price) return

    setText(el, `— ${price}`)
  }

  function currentPriceText() {
    const container = document.querySelector('block-price .block-price__container')
    if (!container) return ''

    const blocks = container.querySelectorAll('.block-price__regular, .block-price__sale')
    const visible = Array.prototype.find.call(blocks, isVisible)
    if (!visible) return ''

    // Each figure is printed twice, once aria-hidden for sighted readers and
    // once visually hidden for screen readers, so read the aria-hidden copy.
    // The struck compare-at price is not the one we want.
    const nodes = Array.prototype.filter.call(
      visible.querySelectorAll('[aria-hidden="true"]'),
      (node) => !node.closest('.compare_at_price')
    )

    const last = nodes[nodes.length - 1]

    return last ? last.textContent.trim() : ''
  }

  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)
  }

  /* ---------------------------------------------------------- lifecycle */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initObxBuyBox)
  } else {
    initObxBuyBox()
  }

  // Theme editor re-renders the section without a page load.
  document.addEventListener('shopify:section:load', initObxBuyBox)
})()
